import React from 'react'
import { Helmet } from 'react-helmet'
import { ROUTES } from '../../lib/constants'
import styles from './LegalPages.module.scss'
import { Banner } from '../../components/Banner/Banner'
import { Link } from 'react-router-dom'

export const ResidentialTerms = (): React.JSX.Element => {
  return (
    <>
      <Helmet>
        <title>{ROUTES.residentialTerms.title}</title>
      </Helmet>

      <div className="grid-12">
        <div className={styles.container}>
          <h1>Terms for Residential Internet Service</h1>
          <section className={styles.content}>
            <p className="italic">Last Updated: July 2023</p>

            <p>
              Thank you for choosing Cityside Networks, LLC (dba Cityside Fiber)
              (&quot;Cityside Fiber&quot;). These{' '}
              <span className="bold">
                Residential Internet Terms of Service
              </span>{' '}
              (&quot;Terms&quot;) along with the applicable policies and
              additional terms provided to you by Cityside Fiber, makes you
              aware of your obligations, the obligations of Cityside Fiber, and
              the rules you and your users must follow when using Cityside
              Fiber&apos;s residential internet service (“Service”). Cityside
              Fiber reserves the right to update or modify these Terms at any
              time and from time to time with or without prior notice. Continued
              use of the Service will be deemed acknowledgment and acceptance of
              the then current Terms.
            </p>

            <p>
              THESE TERMS CONTAINS (1) A BINDING ARBITRATION PROVISION, WHICH
              INCLUDES A WAIVER OF YOUR RIGHT TO BRING CLAIMS AS CLASS ACTIONS;
              (2) A LIMITATION ON YOUR RIGHT TO BRING CLAIMS AGAINST CITYSIDE
              FIBER MORE THAN 1 YEAR AFTER THE RELEVANT EVENTS OCCURRED; AND (3)
              THE RIGHT TO OPT OUT OF THE FOREGOING PROVISIONS. THESE PROVISIONS
              AFFECT YOUR RIGHTS WITH RESPECT TO ALL SERVICES. SEE SECTION 13
              FOR DETAILS ON THESE PROVISIONS.
            </p>

            <p>
              You must accept these Terms as a condition of receiving the
              Service. By accepting these Terms, you confirm you are a capable
              adult over the age of majority. If you are an entity, by accepting
              these Terms, you confirm (through your duly authorized
              representative) that you are a corporation, partnership, or other
              legal entity duly formed (and incorporated if applicable) in good
              standing where required to do business with all legal authority
              and power to accept these Terms; and you are also confirming that
              these Terms constitutes a valid and binding obligation of yours.
              By enrolling in, using, or paying for the Service, you agree to
              the terms and conditions in these Terms.
            </p>

            <ol data-bold-num data-no-indent>
              <li>
                <h2>Equipment &amp; Software</h2>
                <ol type="a">
                  <li>
                    Customer Equipment. Other than the equipment and/or software
                    provided to you by Cityside Fiber for use with the Service
                    (collectively, the &quot;Cityside Fiber Equipment&quot;),
                    you must provide all equipment, devices, and software
                    necessary to receive the Service. Any equipment or software
                    that was not provided to you by Cityside Fiber, is not the
                    responsibility of Cityside Fiber and Cityside Fiber will not
                    provide support for or be responsible for ongoing
                    maintenance of such equipment. Regardless of whether the
                    equipment used to access your Service is owned by you or
                    Cityside Fiber, Cityside Fiber reserves the right to manage
                    such equipment for the duration of your Service and retains
                    exclusive rights to data generated by the equipment. Neither
                    you nor a third party may change, interfere with, or block
                    access to equipment, the data or settings. Cityside Fiber
                    suggests that you use your own router only if you&apos;re
                    comfortable managing it on your own, since we won&apos;t be
                    able to help you troubleshoot it.
                  </li>
                  <li>
                    Cityside Equipment. Cityside Fiber may provide or rent
                    various pieces of Cityside Fiber Equipment to you in
                    connection with the Service. In some cases, to use the
                    provided Cityside Fiber Equipment, you will need to review
                    and agree to terms of service governing use of the Cityside
                    Fiber Equipment. If you lose or damage this Cityside Fiber
                    Equipment after installation, Cityside Fiber may charge you
                    a fee for replacement equipment. Equipment fees may be
                    subject to change. Unless explicitly stated otherwise in
                    writing executed by Cityside Fiber, Cityside Fiber owns all
                    Cityside Fiber Equipment provided by Cityside Fiber in
                    connection with the Service. You authorize Cityside Fiber to
                    install software upgrades on any Cityside Fiber Equipment.
                    You also agree not to use the Cityside Fiber Equipment for
                    any purpose other than using the Services. From time to
                    time, Cityside Fiber may offer you equipment that you may
                    purchase in connection with the Services under separate
                    terms to be provided with such equipment.
                  </li>
                  <li>
                    Reslionsibility for and Return of Equipment. Upon
                    termination of your Service for any reason, you must return
                    the Cityside Fiber Equipment, undamaged, within 30 calendar
                    days to Cityside Fiber. If the equipment is not returned
                    within 30 calendar days, or is returned damaged, you may be
                    charged for the replacement value of the Cityside Fiber
                    Equipment.
                  </li>
                  <li>
                    Service Availability and Limitations. Many factors can
                    affect the availability and quality of your Service,
                    including, but not limited to, capacity limitations such as
                    network capacity, terrain, buildings, foliage, and weather.
                    Service may be interrupted, delayed, or otherwise limited
                    for a variety of reasons, including environmental
                    conditions, unavailability of radio frequency channels,
                    system capacity, network management, priority access by
                    National Security and Emergency Preparedness personnel in
                    the event of a disaster or emergency, coordination with
                    other systems, equipment modifications and repairs, and
                    problems with the facilities of interconnecting carriers.
                    Despite our efforts, we cannot guarantee any particular
                    amount of bandwidth on the Service or that any speed or
                    throughput of your connection will be available at all
                    times. The speed of the Service will vary depending upon a
                    number of factors as noted herein, events beyond Cityside
                    Fiber&apos;s control, and system failures and upgrades.
                    Accordingly, we cannot guarantee that the Service will meet
                    your requirements or expectations or will be uninterrupted,
                    timely, secure, or error free.
                  </li>
                </ol>
              </li>

              <li>
                <h2>Prohibited Network Uses.</h2>
                <ol type="a">
                  <li>
                    We may take any and all reasonable actions necessary to
                    restrict any prohibited network uses, including those
                    addressed in Cityside Fiber&apos;s Acceptable Use Policy
                    (“AUP”) and/or any use in a manner that: (a) hinders other
                    customers&apos; access to the network; (b) involves the
                    installation or similar mechanism to originate, amplify,
                    enhance, retransmit or generate a radio frequency signal
                    without our permission; (c) negatively affects our network
                    or compromises network security or capacity; (d) excessively
                    and disproportionately contributes to network congestion;
                    (e) adversely impacts network service levels or legitimate
                    data flows; (f) degrades network performance; or (g) causes
                    harm to the network or other customers.
                  </li>
                  <li>
                    Cityside Fiber is committed to providing an outstanding user
                    experience to customers that supports lawful product,
                    service, or application that our customers choose. In order
                    to provide such experience, Cityside Fiber implements
                    reasonable network management practices. In the unlikely
                    event that users&apos; aggregate demand exceeds the
                    available capacity of the network, creates acute congestion,
                    excessively and disproportionately contributes to network
                    congestion, adversely impacts network service levels or
                    legitimate data flows or constitutes any other use
                    identified in Section 2(a) above, Cityside Fiber will employ
                    various techniques to ensure that customers continue to have
                    a positive experience. In such times, Cityside Fiber Service
                    bandwidth will be fairly allocated among customers without
                    regard to the customers’ online activities or the protocols
                    or applications that the customers are using.
                  </li>
                </ol>
              </li>

              <li>
                <h2>Access &amp; Installation of Equipment.</h2>
                <p>
                  During the term of the Service, you will provide Cityside Fiber
                  and its subcontractors with reasonable access to your premises
                  in order to install, maintain, and repair the Service and you
                  authorize any other adult resident or guest at your residence to
                  grant access to your premises and property for these purposes.
                  You understand and agree that Cityside Fiber may drill, cut, and
                  otherwise alter improvements on the premises and property
                  (including walls, flooring, soft landscaping and/or other
                  surfaces) in order to install, maintain, or repair the Service.
                  If you do not own your premises and/or property, you warrant
                  that you have obtained permission from any necessary party,
                  including but not limited to the owner, landlord, or building
                  manager, to allow Cityside Fiber and its subcontractors
                  reasonable access to install, maintain, and repair the Service
                  and to make any alterations Cityside Fiber deems appropriate for
                  the work to be performed. You acknowledge that you may be asked
                  to provide written evidence that you have received all
                  permissions necessary to authorize the performance of
                  installation, maintenance, and repair Services. You agree that
                  Cityside Fiber may install equipment on the exterior and
                  interior of your residence (including but not limited to
                  installing, maintaining, and repairing underground conduit
                  and/or affixing equipment to the outside of your residence) at
                  any reasonable location. You agree that Cityside Fiber may take
                  photos of the installation on the exterior or interior of your
                  residence for quality control purposes. You also agree that
                  Cityside Fiber may use, and that you have the necessary
                  permissions to approve Cityside Fiber’s use of, existing
                  facilities, including existing wiring in and around your
                  residence, to complete the installation services. Without
                  limiting any other provisions of these Terms, you agree to
                  indemnify Cityside Fiber from and against all claims by an
                  owner, landlord, building manager, or other party in connection
                  with installation, maintenance, repair, or provision of the
                  Services. If Cityside Fiber incurs any costs or losses,
                  including attorneys’ fees, because you did not get the necessary
                  permission(s) to authorize the installation, maintenance, or
                  repair of the equipment required for the Services, you are
                  responsible for reimbursing Cityside Fiber for those costs or
                  losses. Notwithstanding the foregoing, Cityside Fiber may need a
                  separate agreement with you or your landlord in order to install
                  the Service.
                </p>
              </li>

              <li>
                <h2>Construction Fee</h2>
                <p>
                  Sometimes Cityside Fiber may need to charge a fee in connection
                  with the construction or installation of your network connection
                  (“Construction Fee”). Cityside Fiber will only charge you if
                  (i)Cityside Fiber notifies you of a Construction Fee prior to
                  Cityside Fiber beginning any construction or installation work
                  that is the subject of the Construction Fee or (ii) if relying
                  on your sign up for Service, Cityside Fiber installs a drop
                  outside of your premises for your pending network connection and
                  you fail to provide access within a reasonable amount of time
                  for Cityside Fiber to complete installation (in which case, you
                  agree to pay a drop installation fee of $250 which will be
                  credited towards the Service if network connection is completed
                  within 90 days of the imposition of the drop installation fee).
                  Cityside Fiber may allow for payment of Construction Fees in
                  installments. If you cancel or make certain changes to your
                  Service, or your Service is otherwise terminated due to failure
                  to comply with these Terms, before you have paid the entire
                  Construction Fee, you agree to pay the outstanding Construction
                  Fee balance as of the date of such change, cancellation or
                  termination.
                </p>
              </li>

              <li>
                <h2>Third Party Services.</h2>
                <p>
                  The Service may permit access to products, services, websites,
                  advertisements, and content (&quot;Third Party Content and
                  Services&quot;) from advertisers, publishers, vendors and other
                  third parties (&quot;Third Parties&quot;). Your use of
                  Third-Party Content and Services may be subject to additional
                  terms of use set by the Third Parties. YOUR USE OF THIRD-PARTY
                  CONTENT AND SERVICES IS AT YOUR SOLE RISK AND DISCRETION.
                  Cityside Fiber does not investigate, monitor, represent, endorse
                  or publish the Third-Party Content and Services. Furthermore,
                  Cityside Fiber does not represent or endorse the accuracy or
                  reliability of any opinion, statement or other information
                  provided by any Third Party, or represent or warrant that your
                  use of any Third-Party Content or Services will not infringe the
                  rights of third parties. Cityside Fiber reserves the right to
                  restrict or deny access to any Third-Party Content and Services
                  otherwise accessible through the Service. Cityside Fiber shall
                  have no liability to you arising out of or in connection with
                  your access to and use (or misuse) of the Third-Party Content
                  and Services.
                </p>
              </li>

              <li>
                <h2>Changes to these Terms and/or the Service.</h2>
                <p>
                  We may change these Terms, conditions, rates, fees, or charges
                  regarding your Service at any time. We also reserve the right to
                  modify or discontinue the Service, temporarily or permanently.
                  We will provide you with notice of material changes via either
                  any email address you provide, recorded announcement, bill
                  message, bill insert, newspaper ad, postcard, letter, or call to
                  your billed telephone number. It is your responsibility to check
                  for any such notices. Your continued subscription to the Service
                  after the effective date of the change constitutes your
                  acceptance of such changes. Changes will not apply
                  retroactively. If you do not agree to the modified terms for the
                  Service, you should discontinue your use of the Service and
                  contact Cityside Fiber to change or terminate the Service.
                </p>
              </li>

              <li>
                <h2>Restrictions on Use.</h2>
                <ol type="a">
                  <li>
                    AUP. Your use of the Service is subject to the AUP which may
                    be viewed at www.citysidefiber.com/policies and is hereby
                    incorporated into the Terms as though fully set forth
                    herein. In accordance with the AUP, Cityside Fiber reserves
                    the right to deny, disconnect, modify, and/or terminate,
                    without notice, the Subscriber Account (as defined below) or
                    the Service provided by Cityside Fiber to any customer whose
                    use of the Service violates the AUP.
                  </li>
                  <li>
                    No Resale. The Service is provided for your use only (unless
                    otherwise specifically stated) and you agree not to, whether
                    for a fee or without charge, reproduce, duplicate, copy,
                    sell, transfer, trade, resell, re-provision, redistribute,
                    or rent the Service, your membership in the Service, any
                    portion of the Service, use of the Service, or access to the
                    Service, including, but not limited to, reselling
                    capabilities enabled or used by a specific application
                    (including, without limitation, Voice Over Internet Protocol
                    (VOIP) via wired, wireless, or other means. For example, you
                    agree that the Service is not to be used to trunk or
                    facilitate public internet access (&quot;hotspots&quot;) or
                    any other public use of the Service, or for any high-volume
                    purpose. All aspects of the Service, except that portion
                    provided by third party providers, is copyrighted and
                    property of Cityside Fiber.
                  </li>
                  <li>
                    Copyright Infringement and Digital Millennium Copyright Act.
                    You are prohibited from infringing, publishing, submitting,
                    copying, uploading, downloading, posting, transmitting,
                    reproducing, or distributing software, video or audio
                    content, or any other material that is protected by
                    copyright, trademark, patent, trade secret, any other type
                    of intellectual property rights, trademark laws (by rights
                    of privacy or publicity), or other proprietary right of any
                    party unless you own or control the rights thereto or have
                    received all necessary consent to do the same. This
                    prohibition includes the use of any material or information
                    including images or photographs that are made available
                    through the Service(s). Cityside Fiber assumes no
                    responsibility, and you assume all risks, regarding the
                    determination of whether material is in the public domain,
                    or may otherwise be used by you for such purposes.
                  </li>
                  <li>
                    Use by Children. Cityside Fiber is concerned about the
                    safety and privacy of all its users, particularly children.
                    For this reason, children under the age of 13 should not be
                    permitted to access the Service unless added as a sub
                    account by a Subscriber Account holder who is their legal
                    guardian. You understand that by adding a child to your
                    Subscriber Account, you are giving your child access to
                    features that are available as part of the Service
                    (including email, message boards, clubs, and instant
                    messages) and the Internet. Please remember that the Service
                    is designed to appeal to a broad audience. It is your
                    responsibility to determine whether the features provided
                    through the Service are appropriate for a minor.
                  </li>
                  <li>
                    Privacy Policy. Your use of the Service and registration
                    data and certain other information about you are subject to
                    the respective privacy policies of Cityside Fiber. This
                    policy is available at:{' '}
                    <Link to={ROUTES.privacy.path}>
                      citysidefiber.com{ROUTES.privacy.path}
                    </Link>
                    .
                  </li>
                </ol>
              </li>

              <li>
                <h2>Home Network Management.</h2>
                <p>
                  Although Cityside Fiber reserves the right to manage any
                  equipment used to access any Service, you are solely responsible
                  for all security measures over your in-home network, including
                  any local area network(s) and/or Wi-Fi Equipment. That includes,
                  but is not limited to, access to authorization codes or
                  passwords, as well as any encryption you deem necessary or
                  required. Cityside Fiber may provide you with tools or software
                  to assist you in managing one or more aspects of your home
                  network (which software would then be included in the term
                  &quot;Software&quot; as used herein below), but you remain
                  solely responsible for all aspects of your home network,
                  including any activity by children or other guests that you may
                  allow (either intentionally or unintentionally, through lack of
                  adequate security measures) to access your Services via your
                  home network and/or Home Wi-Fi. For that reason, Cityside Fiber
                  recommends that you take all necessary measures to ensure
                  adequate network security and to closely monitor use of your
                  Services and your home network by anyone accessing your home
                  network, especially children.
                </p>
              </li>

              <li>
                <h2>Registration and Subscription</h2>
                <ol type="a">
                  <li>
                    Subscriber Account. When you complete the registration
                    process for the Service, you become the &quot;Subscriber
                    Account&quot; holder. To be a Subscriber Account holder you
                    must either be: (i) 18 years or older, if an individual, or,
                    (ii) be a corporation, partnership, or other legal entity
                    duly formed (and incorporated if applicable) in good
                    standing where required to do business with all legal
                    authority and power to accept these Terms and acting through
                    your duly authorized representative. You will be asked to
                    choose a unique &quot;Member ID&quot; for your account.
                  </li>
                  <li>
                    Subscriber Account Responsibility. The Subscriber Account
                    holder is responsible for all activity associated with the
                    Subscriber Account and any of its sub accounts, including
                    all fees and charges, whether the charges are incurred by
                    the Subscriber Account or the sub accounts.
                  </li>
                  <li>
                    Registration Data. All information that you provide to
                    Cityside Fiber must be accurate, including your name,
                    address, credit or charge card numbers and expiration dates,
                    and any payment information (&quot;Registration Data&quot;).
                    You are responsible for keeping all Registration Data
                    accurate and must provide changes promptly to Cityside Fiber
                    by logging on to the Cityside Fiber customer online portal.
                  </li>
                  <li>
                    Authority and Authorized User(s). You may authorize spouses,
                    partners, family or other household members or designated
                    persons to act on your behalf in managing your Subscriber
                    Account, including changing or adding equipment and
                    programming: (i) by providing such person access to your
                    confidential account password or passcode; (ii) by updating
                    your account information to add authorized user(s); (iii) by
                    granting authorizations and/or ordering permissions to sub
                    account holder(s); and/or (iv) by granting anyone primary or
                    secondary online access to manage your account (each such
                    person, an &quot;Authorized User&quot;).
                  </li>
                  <li>
                    Password Protections. Your account password or passcode must
                    be provided to engage in most online or telephonically
                    enabled account management functions. You agree to
                    immediately notify Cityside Fiber if your password or
                    passcode has been compromised and/or you wish to remove an
                    Authorized User from your account; in the absence of such
                    notification, you are responsible for activities on your
                    Subscriber Account, including by persons listed as
                    Authorized Users.
                  </li>
                </ol>
              </li>

              <li>
                <h2>Termination or Cancellation of Service</h2>
                <ol type="a">
                  <li>
                    When you change your Service by upgrading or downgrading
                    your subscription, the fees owed will be prorated based upon
                    the date that your Service changes.
                  </li>
                  <li>
                    Your Decision to Terminate or Continue Service. You may
                    cancel the Service at any time. If you cancel Service prior
                    to the end of your billing cycle, your Service cancellation
                    will be effective as of the final day of your billing cycle
                    for billing purposes. You will not receive a refund or
                    prorated credit of any portion of the Service if you cancel
                    prior to the end of your billing cycle. To request a
                    cancellation of Service you may call us at{' '}
                    <a href="tel:+19497792489">949-779-2489</a>.
                  </li>
                  <li>
                    <p>
                      Service Suspension/Termination by Cityside Fiber. Cityside
                      Fiber may immediately terminate or suspend your Subscriber
                      Account and sub accounts, and all or a portion of the
                      Service without notice for any of the reasons set forth in
                      Cityside Fiber&apos;s AUP, or if: (a) you provide false or
                      inaccurate information to Cityside Fiber; (b) you (or
                      anyone you permit to utilize the Service, including a sub
                      account associated with your Subscriber ID) violate these
                      Terms or the Cityside Fiber AUP; (c) you (or anyone you
                      permit to utilize the Service, including a sub account
                      associated with your Subscriber ID) engage in conduct that
                      is a violation of any law, regulation or tariff
                      (including, without limitation, copyright and intellectual
                      property laws); or, (d) if you engage in conduct that is
                      threatening, abusive or harassing to Cityside Fiber,
                      employees or any of their vendor&apos;s employees or
                      representatives, including, for example, making threats to
                      physically harm or damage employee or company property,
                      frequent use of profane or vulgar language, or repeatedly
                      contacting our customer service representatives for
                      reasons that do not pertain to our provisioning,
                      maintenance, repair, or general servicing of your Service
                      after you have been asked to stop such conduct. You may
                      also be subject to suspension or termination pursuant to
                      Section 2 and 7.
                    </p>
                    <p>
                      If we terminate or suspend the Service, your license to
                      use any software provided in connection with the Service
                      is also terminated or suspended (as applicable). If the
                      Service is terminated, Cityside Fiber has the right to
                      immediately delete all data, files, and other information
                      stored in or for your account without further notice to
                      you. You must pay all charges for the Service through the
                      end of the billing cycle when the Service is terminated.
                    </p>
                  </li>
                  <li>
                    Suspension and Termination for Non-payment. Cityside Fiber
                    may suspend or terminate Service if your payment is past due
                    any time following the 6th day payment is past due.
                  </li>
                  <li>
                    Suspension and/or Termination upon Loss of Access. Upon any
                    interruption or loss of either your or Cityside Fiber&apos;s
                    rights to access any part of the network facilities required
                    to provide the Service, including the interruption or loss
                    of any rights to access the land or buildings in which the
                    facilities are located, Cityside Fiber may, in its sole
                    discretion, suspend or terminate all or any portion of your
                    Service. In the event of any interruption or loss of access,
                    Cityside Fiber will endeavor to provide you with reasonable
                    advanced notice of any suspension or termination of the
                    Service, however the timing of any suspension or
                    termination, as well as the timing of any resumption of
                    Services, are entirely at Cityside Fiber&apos;s reasonable
                    discretion.
                  </li>
                  <li>
                    Restoral Fee and Payment of Past Due Amounts. If either you
                    or Cityside Fiber suspends the Service for any reason set
                    forth herein (other than due to Cityside Fiber’s loss of
                    access), you must make satisfactory arrangements to pay all
                    past due amounts in order to have the Service restored. You
                    may also be required to pay a Service Restoral Fee of no
                    more than $50 per incident of suspension or termination of a
                    particular Service (subject to applicable law and except as
                    may otherwise have been expressly agreed in writing). The
                    Restoral Fee, if applicable, will be assessed on the next
                    monthly bill you receive following the resumption of
                    Service.
                  </li>
                </ol>
              </li>

              <li>
                <h2>Payment.</h2>
                <p>
                  You agree to pay in full each month: (1) the monthly fee
                  specified for the Service; (2) any charge(s) for equipment
                  required for the Service, if any; (3) activation fees,
                  connection charges, and/or construction fees, if any; (4) late
                  fees, Service Restoral Fees, and other applicable Service
                  charges; and, (5) any applicable local, state, or federal taxes,
                  fees, and surcharges imposed or levied on or with respect to the
                  Service, the equipment, wiring, any purchased devices, conveyed
                  devices, or installation or service charges incurred with
                  respect to the same (including franchise fees)
                </p>
                <ol type="a">
                  <li>
                    Billing and Method of Payment. Billing will begin when the
                    installation is complete. You will automatically receive an
                    online bill (see below). You may be asked to provide us with
                    a credit card number from a card issuer that we accept, a
                    valid debit card number that you own, or electronic funds
                    transfer information (i.e. bank account information), in
                    order to activate your Service. You hereby authorize
                    Cityside Fiber to charge/debit the bank account, the credit
                    or debit card that you designated with respect to all
                    applicable fees for the Services you purchase, whether
                    ordered by you, someone authorized by you or someone with
                    access to the Services pursuant to your Cityside Fiber
                    account or any related equipment. You authorize the issuer
                    of the bank account, the credit or debit card to pay any
                    amounts described herein without requiring a signed receipt,
                    and you agree that these Terms shall be accepted as
                    authorization to the issuer of the bank account, the credit
                    or debit card to pay any amounts described herein without
                    requiring a signed receipt, and you agree that these Terms
                    shall be accepted as authorization to the issuer of the bank
                    account, the credit or debit card to pay all such amounts.
                    You authorize Cityside Fiber and/or any other company who
                    bills products or Services, or acts as billing agent for
                    Cityside Fiber, to continue to attempt to charge and/or
                    place holds with respect to all sums that become due and are
                    described herein, or any portion thereof, to your bank
                    account, the credit or debit card until such amounts are
                    paid in full. You agree to provide Cityside Fiber with
                    updated bank account, the credit or debit card information
                    upon the request of Cityside Fiber and any time the
                    information you previously provided is no longer valid. You
                    acknowledge and agree that neither Cityside Fiber nor any
                    Cityside Fiber affiliated company will have any liability
                    whatsoever for any non-sufficient funds or other charges
                    incurred by you as a result of such attempts to charge,
                    and/or place holds on, your bank account, the credit or
                    debit card. In the event you are enrolled, or later enroll,
                    in an automatic payment or electronic funds transfer plan,
                    you agree that all sums that become due and are described
                    herein may be charged, at the option of Cityside Fiber, to
                    the account number provided for such automatic payment or
                    electronic funds transfer plan. When payment is made by
                    credit card or debit card, payment will also be subject to
                    the terms and conditions established by the credit or debit
                    card issuer. If charges cannot be processed through your
                    credit card, or if your bank draft or electronic funds
                    transfer is returned for insufficient funds, we will charge
                    you an additional fee of $30 per transaction. You agree that
                    Cityside Fiber is not responsible for any third-party
                    charges you may incur in connection with your credit/debit
                    card or other payment method.
                  </li>
                  <li>
                    Late Payment Charge. You agree that for each bill not paid
                    in full by the third calendar day following the payment due
                    date, a Late Payment Charge will be assessed of no more than
                    $10 per bill (subject to applicable law).
                  </li>
                  <li>
                    Bill Inquiries. If you believe you have been billed in error
                    for your Services or any related equipment, please notify us
                    within 60 days of the billing date by contacting Customer
                    Service. Cityside Fiber will not issue refunds or credits
                    after the expiration of this 60-day period, except where
                    required by law.
                  </li>
                  <li>
                    Refunds and Credits. Except as otherwise required by
                    applicable law, any amounts refunded in the form of bill
                    credits, cash payments, or any other form shall be inclusive
                    of all applicable taxes that were originally paid on such
                    amounts. Credit amounts, that do not represent a refund of,
                    or a discount to, the price paid for any good or service
                    will not result in the refund of any previously paid tax,
                    fee, or surcharge.
                  </li>
                </ol>
                <br />
                CITYSIDE FIBER&apos;S SOLE AND ENTIRE LIABILITY AND YOUR
                EXCLUSIVE REMEDY FOR THE PROCESSING OR DEDUCTION OF AN INCORRECT
                TRANSFER OF FUNDS HEREUNDER SHALL BE THE RETURN TO YOU OF THE
                IMPROPERLY TRANSFERRED FUNDS. FOR THE AVOIDANCE OF DOUBT, THIS
                SECTION 11 DOES NOT LIMIT ANY NON-WAIVABLE RIGHTS YOU MAY HAVE
                UNDER APPLICABLE LAW.
              </li>

              <li>
                <h2>Service and Maintenance Procedure.</h2>
                <p>
                  In the event of a problem with your Service or Cityside Fiber
                  Equipment, you agree to contact Cityside Fiber customer service
                  at <a href="tel:+19497792489">(949) 779-2489</a>. Depending on
                  the nature of the problem, we may need to schedule an
                  appointment for our service concierge to visit your service
                  address. We will not charge for service visits if the Cityside
                  Fiber Equipment or Services caused the problem. You agree to
                  allow Cityside Fiber employees or agents to inspect, repair or
                  replace Cityside Fiber Equipment or wiring, both inside and
                  outside the service address. In the event that Cityside Fiber
                  determines that the problem was caused by your equipment, your
                  negligence or intentional act, your software, Customer-installed
                  wiring or hardware, or a pet or domestic animal, then Cityside
                  Fiber may charge for the service visit and any repairs or
                  replacement the Cityside Fiber Equipment. Cityside Fiber
                  reserves the right not to replace the Cityside Fiber Equipment
                  or Service if a condition exists that will result in further
                  damage to the Cityside Fiber Equipment until the condition is
                  corrected.
                </p>
              </li>

              <li>
                <h2>
                  DISPUTE RESOLUTION WITH CITYSIDE FIBER BY BINDING ARBITRATION
                </h2>
                <span className="italic">
                  PLEASE READ THIS CAREFULLY. IT AFFECTS YOUR RIGHTS.
                </span>
                <p>
                  Most customer concerns can be resolved quickly and to the
                  customer&apos;s satisfaction by calling our customer service
                  department at <a href="tel:+19497792489">(949) 779-2489</a>.{' '}
                  <span className="bold">
                    In the unlikely event that the Cityside Fiber customer
                    service department is unable to resolve a complaint you may
                    have to your satisfaction (or if Cityside Fiber has not been
                    able to resolve a dispute it has with you after attempting
                    to do so informally), we each agree to resolve those
                    disputes through binding arbitration or small claims court
                    instead of in courts of general jurisdiction. Any
                    arbitration under these Terms will take place on an
                    individual basis; class arbitrations and class actions are
                    not permitted.
                  </span>{' '}
                  For any non-frivolous claim that does not exceed $75,000,
                  Cityside Fiber will pay all costs of the arbitration.
                  Moreover, in arbitration you are entitled to recover
                  attorneys&apos; fees from Cityside Fiber to at least the same
                  extent as you would be in court. &nbsp;In addition, under
                  certain circumstances (as explained below), Cityside Fiber
                  will pay you more than the amount of the arbitrator&apos;s
                  award and will pay your attorney (if any) twice his or her
                  reasonable attorneys&apos; fees if the arbitrator awards you
                  an amount that is greater than what Cityside Fiber has offered
                  you to settle the dispute.
                </p>
                <h3>Arbitration Terms</h3>
                <p>
                  Subject to the &quot;Exclusions&quot; paragraph below,
                  Cityside Fiber and you agree to arbitrate disputes and claims
                  arising out of or relating to these Terms, the Services, the
                  Equipment, or marketing of the Services you have received from
                  Cityside Fiber. Notwithstanding the foregoing, either party
                  may bring an individual action on any matter or subject in
                  small claims court.
                </p>
                THESE GENERAL TERMS MEMORIALIZE A TRANSACTION IN INTERSTATE
                COMMERCE.THE FEDERAL ARBITRATION ACT GOVERNS THE INTERPRETATION
                AND ENFORCEMENT OF THESE ARBITRATION PROVISIONS.
                <p>
                  A party who intends to seek arbitration must first send to the
                  other a written notice of intent to arbitrate, entitled
                  &quot;Notice of Intent to Arbitrate&quot;
                  (&quot;Notice&quot;). The Notice to Cityside Fiber should be
                  addressed to: Legal Department, Cityside Networks, LLC dba
                  Cityside Fiber, 100 Spectrum Center Drive, Suite 500, Irvine,
                  CA 92618 (&quot;Arbitration Notice Address&quot;). The Notice
                  must: (1) describe the nature and basis of the claim or
                  dispute; and (ii) set forth the specific relief sought. If we
                  do not reach an agreement to resolve the claim within 30 days
                  after the Notice is received, you or Cityside Fiber may
                  commence an arbitration proceeding, in which all issues are
                  for the arbitrator to decide (including the scope of the
                  arbitration clause), but the arbitrator shall be bound by the
                  terms of these Terms.
                </p>
                <p>
                  The arbitration shall be governed by the Commercial
                  Arbitration Rules and the Supplementary Procedures for
                  Consumer Related Disputes (collectively, &quot;AAA
                  Rules&quot;) of the American Arbitration Association
                  (&quot;AAA&quot;), as modified by these Terms, and the
                  arbitration shall be administered by the AAA. The AAA Rules
                  and fee information are available at &quot;www.adr.org,&quot;
                  by calling the AAA at 1-800-778-7879, or by writing to the
                  Arbitration Notice Address.
                </p>
                <p>
                  CITYSIDE FIBER SHALL BEAR THE COST OF ANY ARBITRATION FILING
                  FEES AND ARBITRATOR&apos;S FEES FOR CLAIMS OF UP TO $75,000.
                  SUBSCRIBER IS RESPONSIBLE FOR ALL OTHER ADDITIONAL COSTS THAT
                  SUBSCRIBER INCURS IN THE ARBITRATION INCLUDING, BUT NOT
                  LIMITED TO, ATTORNEYS FEES OR EXPERT WITNESS COSTS UNLESS
                  OTHERWISE REQUIRED OF CITYSIDE FIBER UNDER APPLICABLE LAW.
                </p>
                <p>
                  If the arbitrator&apos;s award exceeds $75,000, either party
                  may appeal such award to a three-arbitrator panel administered
                  by the AAA and selected according to the AAA Rules, by filing
                  a written notice of appeal within 30 days after the date of
                  entry of the arbitration award. The appealing party must
                  provide the other party with a copy of such appeal
                  concurrently with its submission of the appeals notice to AAA.
                  The three-arbitrator panel must issue its decision within 120
                  days of the date of the appealing party&apos;s notice of
                  appeal. The decision of the three-arbitrator panel shall be
                  final and binding, except for any appellate right which may
                  exist under the Federal Arbitration Act.
                </p>
                <p>
                  The parties may agree that arbitration will be conducted
                  solely on the basis of the documents submitted to the
                  arbitrator, via a telephonic hearing, or by an in-person
                  hearing as established by AAA rules.
                </p>
                <p>
                  YOU AGREE THAT, BY ENTERING INTO THESE TERMS, YOU AND CITYSIDE
                  FIBER ARE WAIVING THE RIGHT TO A TRIAL BY JUDGE OR JURY.
                </p>
                <p>
                  Unless Cityside Fiber and you agree otherwise in writing, all
                  hearings conducted as part of the arbitration shall take place
                  in the county of your billing address.
                </p>
                <p>
                  The arbitrator may award injunctive relief only in favor of
                  the party seeking relief, only to the extent sought, and only
                  to the extent necessary to provide the specific relief
                  warranted by such individual&apos;s claim.
                </p>
                <p>
                  The parties agree that the arbitrator must give effect to the
                  terms of these Terms.
                </p>
                <p>
                  YOU AND CITYSIDE FIBER AGREE THAT CLAIMS MAY ONLY BE BROUGHT
                  IN YOUR INDIVIDUAL CAPACITY AND NOT ON BEHALF OF, OR AS PART
                  OF, A CLASS ACTION OR REPRESENTATIVE PROCEEDING
                </p>
                <p>
                  Furthermore, unless both you and Cityside Fiber agree
                  otherwise in writing, the arbitrator may not consolidate
                  proceedings or more than one person&apos;s claims and may not
                  otherwise preside over any form of representative or class
                  proceeding. If this specific paragraph is found to be
                  unenforceable, then the entirety of these arbitration
                  provisions shall be null and void and rendered of no further
                  effect with respect to the specific claim at issue.
                </p>
                <p>
                  Right to Opt Out. If you do not wish to be bound by these
                  arbitration provisions, you must notify Cityside Fiber in
                  writing within 30 days of the date you first subscribe to the
                  Service(s). You may opt out by mail to the Arbitration Notice
                  Address. Your written notification to Cityside Fiber must
                  include your name, address, and Cityside Fiber account number
                  as well as a clear statement that you do not wish to resolve
                  disputes with Cityside Fiber through arbitration. Your
                  decision to opt out of this arbitration provision will have no
                  adverse effect on your relationship with Cityside Fiber or the
                  delivery of Services to you by Cityside Fiber.
                </p>
                <p>
                  Severability. If any clause within these arbitration
                  provisions is found to be illegal or unenforceable, that
                  specific clause will be severed from these arbitration
                  provisions, and the remainder of the arbitration provisions
                  will be given full force and effect.
                </p>
                <p>
                  NOTWITHSTANDING ANYTHING TO THE CONTRARY, IN THE EVENT SOME OR
                  ALL OF THESE ARBITRATION PROVISIONS IS DETERMINED TO BE
                  UNENFORCEABLE FOR ANY REASON, OR IF A CLAIM IS BROUGHT THAT IS
                  FOUND BY A COURT TO BE EXCLUDED FROM THE SCOPE OF THESE
                  ARBITRATION PROVISIONS, BOTH PARTIES AGREE TO WAIVE, TO THE
                  FULLEST EXTENT ALLOWED BY LAW, ANY TRIAL BY JURY.
                </p>
                <p>
                  For purposes of the foregoing sentence only, in the event such
                  waiver is found to be unenforceable, it shall be severed from
                  these Terms, rendered null and void and of no further effect
                  without affecting the rest of the arbitration provisions set
                  forth herein.
                </p>
                <p>
                  EXCLUSIONS. YOU AND CITYSIDE FIBERAGREE THAT THE FOLLOWING
                  CLAIMS OR DISPUTES SHALL NOT BE SUBJECT TO ARBITRATION:
                </p>
                <p>
                  (1) ANY INDIVIDUAL ACTION BROUGHT BY YOU OR BY CITYSIDE FIBER
                  ON ANY MATTER OR SUBJECT THAT IS WITHIN THE JURISDICTION OF A
                  COURT THAT IS LIMITED TO ADJUDICATING SMALL CLAIMS.
                </p>
                <p>
                  (2) ANY DISPUTE OVER THE VALIDITY OF ANY PARTY&apos;S
                  INTELLECTUAL PROPERTY RIGHTS.
                </p>
                <p>
                  (3) ANY DISPUTE RELATED TO OR ARISING FROM ALLEGATIONS
                  ASSOCIATED WITH UNAUTHORIZED USE OR RECEIPT OF SERVICE.
                </p>
                <p>
                  The foregoing arbitration provisions shall survive the
                  termination of these Terms.
                </p>
                <p>
                  YOU HAVE ONE YEAR TO BRING A CLAIM AGAINST CITYSIDE FIBER,
                  UNLESS YOU OPT OUT. Subscriber must bring any claim against
                  Cityside Fiber within one (1) year after the date on which the
                  claim arose or, unless applicable law provides that the normal
                  statute of limitations for that claim may not be shortened by
                  Terms. If you do not bring a claim within this period, you
                  waive, to the fullest extent permitted by law, all rights you
                  have to such claim and Cityside Fiber will have no liability
                  with respect to such claim.
                </p>
                <p>
                  You may opt out of this Section, in which case the normal
                  statute of limitations will apply. To opt out, you must notify
                  Cityside Fiber in writing by sending a letter to Slice
                  addressed to Legal Department, Cityside Networks, LLC dba
                  Cityside Fiber, 100 Spectrum Center Drive, Suite 500, Irvine,
                  CA 92618, within 30 days of the date that you first subscribe
                  to the Service. Your written notice must include your name,
                  address, and Cityside Fiber account number as well as a clear
                  statement that you do not wish this Section to apply.
                </p>
              </li>

              <li>
                <h2>Operational Limits.</h2>
                Provisioning of the Service is subject to the availability and
                the operational limitations of the requisite equipment and
                associated facilities. You understand and agree that temporary
                interruptions of the Service may occur as normal events in the
                provision of the Service and that Cityside Fiber is not liable
                for such interruptions. In addition, Cityside Fiber is not
                liable for any failure of performance due to any cause beyond
                their reasonable control including acts of God, fire, explosion,
                vandalism, terrorism, cable cut, major weather disturbance,
                national emergencies, riots, wars, labor difficulties, supplier
                failures, shortages, breaches, any law, order, regulation,
                direction, action, or request by any government, civil, or
                military authority, or suspension of existing service in
                compliance with state and/or federal law, rules, and
                regulations, or delays caused by you or your equipment.
              </li>

              <li>
                <h2>DISCLAIMER OF WARRANTIES</h2>
                <p>
                  YOU EXPRESSLY UNDERSTAND AND AGREE THAT:
                </p>
                <ol>
                  <li>
                    YOUR USE OF THE SERVICE, AND/OR SOFTWARE IS AT YOUR SOLE
                    RISK. THEY ARE PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS
                    AVAILABLE&quot; BASIS WITHOUT WARRANTIES OR GUARANTIES OF
                    ANY KIND. CITYSIDE FIBERAND ITS SUBSIDIARIES, AFFILIATES,
                    OFFICERS, EMPLOYEES, AGENTS, PARTNERS, AND LICENSORS
                    EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER
                    EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED
                    WARRANTIES OF TITLE MERCHANTABILITY, FITNESS FOR A
                    PARTICULAR PURPOSE, AND NON-INFRINGEMENT, AND ANY WARRANTIES
                    IMPLIED BY A COURSE OF PERFORMANCE, COURSE OF DEALING, OR
                    USAGE OF TRADE. YOU BEAR THE ENTIRE RISK AS TO THE QUALITY
                    AND PERFORMANCE OF THE EQUIPMENT AND ARE RESPONSIBLE FOR THE
                    ENTIRE COST OF ANY NECESSARY REPAIR.
                  </li>
                  <li>
                    CITYSIDE FIBER AND ITS SUBSIDIARIES, AFFILIATES, OFFICERS,
                    EMPLOYEES, AGENTS, PARTNERS, AND LICENSORS MAKE NO WARRANTY
                    THAT (i) THE SERVICE, AND/OR SOFTWARE WILL MEET YOUR
                    REQUIREMENTS, (ii) THE SERVICE, AND/OR SOFTWARE WILL BE
                    UNINTERRUPTED, TIMELY, SECURE, CURRENT, COMPLETE OR
                    ERROR-FREE, (iii) THE RESULTS THAT MAY BE OBTAINED FROM THE
                    USE OF THE SERVICE, AND/OR SOFTWARE WILL BE ACCURATE OR
                    RELIABLE, (iv) THE QUALITY OF ANY PRODUCTS, SERVICES,
                    INFORMATION, OR OTHER MATERIAL PURCHASED OR OBTAINED BY YOU
                    THROUGH THE SERVICE, AND/OR SOFTWARE WILL MEET YOUR
                    EXPECTATION, (v) ANY ERRORS IN THE SERVICE, AND/OR SOFTWARE
                    WILL BE CORRECTED AND (vi) THE SERVICE AND/OR SOFTWARE IS
                    FREE OF VIRUSES OR OTHER DISABLING DEVISES OF HARMFUL
                    COMPONENTS.
                  </li>
                  <li>
                    ANY MATERIAL DOWNLOADED OR OTHERWISE OBTAINED THROUGH THE
                    USE OF THE SERVICE, AND/OR SOFTWARE IS DONE AT YOUR OWN
                    DISCRETION AND RISK, AND YOU WILL BE SOLELY RESPONSIBLE FOR
                    ANY DAMAGE TO YOUR COMPUTER SYSTEM OR LOSS OF DATA THAT
                    RESULTS FROM THE DOWNLOAD OF ANY SUCH MATERIAL.
                  </li>
                  <li>
                    NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED
                    BY YOU FROM CITYSIDE FIBEROR THROUGH OR FROM THE SERVICE,
                    AND/OR SOFTWARE WILL CREATE ANY WARRANTY NOT EXPRESSLY
                    STATED IN THESE TERMS.
                  </li>
                </ol>
              </li>

              <li>
                <h2>LIMITATION OF LIABILITY</h2>
                <p>
                  YOU EXPRESSLY UNDERSTAND AND AGREE THAT:
                </p>
                <p>
                  YOU EXPRESSLY UNDERSTAND AND AGREE THAT NEITHER CITYSIDE
                  FIBERNOR ITS SUBSIDIARIES, AFFILIATES, OFFICERS, EMPLOYEES,
                  AGENTS, PARTNERS, OR LICENSORS WILL BE LIABLE TO YOU FOR ANY
                  INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY
                  DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF
                  PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSES (EVEN
                  IF CITYSIDE FIBER HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
                  DAMAGES), RESULTING FROM: (a) THE USE OR THE INABILITY TO USE
                  THE SERVICE, THE EQUIPMENT AND/OR SOFTWARE; (b) THE COST OF
                  PROCUREMENT OF SUBSTITUTE GOODS AND SERVICES; (c) UNAUTHORIZED
                  ACCESS TO OR ALTERATION OF YOUR TRANSMISSIONS OR DATA; (d)
                  STATEMENTS OR CONDUCT OF ANY THIRD PARTY ON THE SERVICE,
                  AND/OR SOFTWARE; (e) FAILURE TO INSURE THE COMPATIBILITY OF
                  YOUR SYSTEM (INCLUDING THE EQUIPMENT, DEVICES, AND SOFTWARE
                  THAT YOU PROVIDE TO RECEIVE THE SERVICE) WITH THE SERVICE,
                  AND/OR SOFTWARE; (f) ANY OTHER MATTER RELATING TO THE SERVICE,
                  AND/OR SOFTWARE; AND/OR (g) BATTERY BACKUP.
                </p>
                <h3>Exclusions and Limitations.</h3>
                <p>
                  SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN
                  WARRANTIES OR THE LIMITATION OR EXCLUSION OF LIABILITY FOR
                  INCIDENTAL OR CONSEQUENTIAL DAMAGES. ACCORDINGLY, SOME OF THE
                  ABOVE LIMITATIONS OF SECTIONS 15 AND 16 MAY NOT APPLY TO YOU.
                </p>
              </li>

              <li>
                <h2>Indemnity.</h2>
                <p>
                  You agree to indemnify and hold Cityside Fiber, and its
                  subsidiaries, affiliates, officers, agents, co-branders,
                  licensors, or other partners and employees harmless from any
                  claim or demand, including reasonable attorneys&apos; fees, made
                  by any third party due to or arising out of content you submit,
                  post, transmit, or otherwise make available through the Service,
                  your use of the Service, your connection to the Service, your
                  violation of these Terms, your violation of the AUP, or your
                  violation of any rights of another. You acknowledge that you are
                  responsible for all use of the Service using your account,
                  including use by sub accounts, and that these Terms, the
                  Acceptable Use Policy, and Privacy Policy, as amended from time
                  to time, apply to any and all usage of your account, including
                  use by sub accounts. You agree to abide by these Terms and you
                  agree to defend, hold harmless, and indemnify Cityside Fiber
                  from and against any and all claims stemming from usage of this
                  account and any sub account - whether or not such usage is
                  expressly authorized by you.
                </p>
              </li>

              <li>
                <h2>Survival.</h2>
                <p>
                  Obligations and rights in connection with these Terms, which by
                  their nature would continue beyond the termination, cancelation,
                  or expiration of these Terms, will survive the termination,
                  cancelation, or expiration of these Terms.
                </p>
              </li>

              <li>
                <h2>Consent to Contact.</h2>
                <p>
                  You expressly authorize, and specifically consent to allowing,
                  Cityside Fiber and/or its affiliates, or any other agents acting
                  by or on behalf of Cityside Fiber (collectively, the
                  &quot;Cityside Fiber Parties&quot;) to contact you with any
                  non-promotional account, informational, or Service-related
                  purpose such as, without limitation, collections and billing,
                  appointment scheduling or confirmations, service alerts,
                  security or network issues, or fraud violations. Subject to any
                  limitations under applicable law, Subscriber may not opt-out of
                  or revoke its consent to receive such communications and
                  informational messages regarding the account. You agree that
                  such contact may be made to any mailing address, telephone
                  number, cellular phone number, e-mail address, or any other
                  electronic address including a number for a cellular phone or
                  other wireless device or service (collectively, your
                  &quot;Contact Information&quot;) that you have provided, or may
                  in the future provide, to Cityside Fiber or any Cityside Fiber
                  Party, or which any Cityside Fiber Party otherwise identifies as
                  your Contact Information, and to any and all telephone numbers,
                  wireless devices or electronic addresses billed on your Cityside
                  Fiber account. You expressly consent and agree that such contact
                  may be made using, among other methods, pre-recorded or
                  artificial voice messages delivered by an automatic telephone
                  dialing system, text messages delivered by an automated system,
                  pre-set e-mail messages delivered by an automatic e-mailing
                  system, or any other pre-set electronic messages delivered by
                  any other automatic electronic messaging system, regardless of
                  whether you incur charges as a result. You agree to provide
                  true, accurate, current and complete contact information to
                  Cityside Fiber and its authorized agents and to promptly update
                  your contact information to keep it true, accurate and complete.
                  In addition to the foregoing, you agree that the Cityside Fiber
                  Parties may contact you using your Contact Information for
                  marketing or promotional services associated with the Service.
                  If you do not wish to receive these optional calls and SMS text
                  messages, you do not have to do so. Your consent to receiving
                  these calls and SMS text messages is not required in order to
                  purchase or receive any Services. To revoke your consent to
                  receive marketing telephone calls and SMS text messages, please
                  let us know. The easiest way is by changing your settings in
                  your Cityside Fiber account, or by calling our customer service
                  team at <a href="tel:+19497792489">(949) 779-2489</a>. You have
                  the right to restrict the use of your Contact Information for
                  marketing purposes, as the Cityside Fiber Privacy Policy
                  explains.
                </p>
              </li>
            </ol>
          </section>
        </div>
      </div>

      <Banner
        headline="Ready to make the switch?"
        text="Let&rsquo;s get you signed up today!"
        btnText="View Internet Packages"
        btnLink={ROUTES.gigInternet.path}
      />
    </>
  )
}
